<template>
  <a-modal
    :title="title"
    :width="800"
    :visible="visible"
    :confirmLoading="confirmLoading"
    :maskClosable="false"
    @ok="handleSubmit"
    @cancel="handleCancel"
    :okText="isEdit ? l('Update') : l('AuditTeam_Create')"
    :cancelText="l('Cancel')"
  >
    <a-spin :spinning="confirmLoading">
      <a-form :form="form" :label-col="labelCol" :wrapper-col="wrapperCol">

        <!-- 基本信息 -->
        <a-divider orientation="left">{{l('AuditTeam_BasicInfo')}}</a-divider>

        <a-form-item :label="l('AuditTeam_TeamName')" has-feedback>
          <a-input
            v-decorator="[
              'name',
              {
                rules: [
                  { required: true, message: l('AuditTeam_NameRequired') },
                  { max: 50, message: l('AuditTeam_NameMaxLength', 50) }
                ]
              }
            ]"
            :placeholder="l('AuditTeam_PleaseInputTeamName')"
          />
        </a-form-item>

        <a-form-item :label="l('AuditTeam_Description')">
          <a-textarea
            v-decorator="[
              'description',
              {
                rules: [
                  { max: 500, message: l('AuditTeam_DescriptionMaxLength', 500) }
                ]
              }
            ]"
            :rows="4"
            :placeholder="l('AuditTeam_PleaseInputDescription')"
          />
        </a-form-item>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="l('AuditTeam_TeamLeader')" has-feedback>
              <a-select
                v-decorator="[
                  'leaderId',
                  {
                    rules: [
                      { required: true, message: l('AuditTeam_LeaderRequired') }
                    ]
                  }
                ]"
                :placeholder="l('AuditTeam_TeamLeader')"
                show-search
                :filter-option="filterOption"
                :loading="usersLoading"
              >
                <a-select-option
                  v-for="user in availableUsers"
                  :key="user.id"
                  :value="user.id"
                  :label="`${user.userName}`"
                >
                  <div class="user-option">
                    <span class="user-name">{{ user.userName }}</span>
                    <span v-if="user.name && user.name !== user.userName" class="real-name">{{ user.name }}</span>
                    <span v-if="user.jobNumber" class="job-number">[{{ user.jobNumber }}]</span>
                  </div>
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item :label="l('AuditTeam_TeamStatus')">
              <a-select
                v-decorator="[
                  'status',
                  {
                    initialValue: 1,
                    rules: [
                      { required: true, message: l('AuditTeam_StatusRequired') }
                    ]
                  }
                ]"
                :placeholder="l('AuditTeam_PleaseSelectStatus')"
              >
                <a-select-option :value="1">{{l('Enabled')}}</a-select-option>
                <a-select-option :value="0">{{l('Disabled')}}</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="l('SortCode')">
              <a-input-number
                v-decorator="[
                  'sortCode',
                  {
                    initialValue: 100,
                    rules: [
                      { type: 'number', message: l('AuditTeam_SortCodeInvalid') },
                      { type: 'number', min: 0, message: l('AuditTeam_SortCodeMin', 0) }
                    ]
                  }
                ]"
                :min="0"
                :max="999999"
                :placeholder="l('AuditTeam_PleaseInputSortCode')"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        
      </a-form>
    </a-spin>
  </a-modal>
</template>

<script>
import AppComponentBase from '../../../shared/component-base/app-component-base.js'
import { AuditTeamServiceProxy } from '../../../shared/service-proxies'
import { LayoutUserServiceProxy } from '../../../shared/service-proxies'

export default {
  mixins: [AppComponentBase],
  name: 'CreateEditAuditTeam',
  data() {
    return {
      // 模态框状态
      visible: false,
      confirmLoading: false,
      isEdit: false,
      editingId: null,

      // 表单配置
      labelCol: {
        xs: { span: 24 },
        sm: { span: 6 }
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 }
      },

      // 可选用户列表
      availableUsers: [],
      usersLoading: false,

      // 服务代理
      serviceProxy: null,
      layoutUserServiceProxy: null
    }
  },
  computed: {
    title() {
      return this.isEdit ? this.l('AuditTeam_EditTitle') : this.l('AuditTeam_CreateTitle')
    }
  },
  created() {
    this.serviceProxy = new AuditTeamServiceProxy(this.$apiUrl, this.$api)
    this.layoutUserServiceProxy = new LayoutUserServiceProxy(this.$apiUrl, this.$api)
  },
  methods: {
    // 显示模态框
    open(id = null) {
      this.visible = true
      this.isEdit = !!id
      this.editingId = id

      // 加载可选用户列表
      this.loadAvailableUsers()

      if (this.isEdit) {
        // 编辑模式，先加载表单数据
        this.loadFormData(id)
      } else {
        // 新建模式，设置默认值
        this.$nextTick(() => {
          this.form.resetFields()
          this.form.setFieldsValue({
            status: 1,
            sortCode: 100
          })
        })
      }
    },

    // 加载表单数据（编辑模式）
    loadFormData(id) {
      this.confirmLoading = true
      this.serviceProxy.getForEdit(id)
        .then((result) => {
          this.$nextTick(() => {
            this.form.setFieldsValue({
              name: result.name,
              description: result.description,
              leaderId: result.leaderId,
              status: result.status,
              sortCode: result.sortCode || 100
            })
          })
          this.confirmLoading = false
        })
        .catch((error) => {
          console.error('获取数据失败:', error)
          this.$message.error(this.l('AuditTeam_GetDataFailed') + (error.message || error))
          this.confirmLoading = false
          this.visible = false
        })
    },

    // 加载可选用户列表
    loadAvailableUsers() {
      this.usersLoading = true
      // 使用undefined作为orgId参数来获取所有用户，与用户页面保持一致
      this.layoutUserServiceProxy.getPaged(undefined, undefined, undefined, 1000, 0)
        .then(result => {
          this.availableUsers = (result.items || []).map(user => ({
            id: user.userId,
            name: user.realName || user.name,
            userName: user.userName || user.domainAccount || '',
            jobNumber: user.jobNumber || ''
          }))
        })
        .catch(error => {
          console.error('获取用户列表失败:', error)
          this.$message.error(this.l('AuditTeam_GetUserListFailed') + (error.message || error))
          this.availableUsers = []
        })
        .finally(() => {
          this.usersLoading = false
        })
    },

    // 用户搜索过滤
    filterOption(input, option) {
      const user = this.availableUsers.find(u => u.id === option.componentOptions.props.value)
      if (!user) return false

      const searchText = input.toLowerCase()
      return (
        user.name.toLowerCase().includes(searchText) ||
        user.userName.toLowerCase().includes(searchText) ||
        (user.jobNumber && user.jobNumber.toLowerCase().includes(searchText))
      )
    },

    // 提交表单
    handleSubmit() {
      const { form } = this
      this.confirmLoading = true

      form.validateFields((errors, values) => {
        if (errors) {
          this.confirmLoading = false
          return
        }

        // 构建提交数据
        const submitData = {
          ...values
        }

        if (this.isEdit) {
          submitData.id = this.editingId
        }

        // 调用API保存数据
        if (this.isEdit) {
          // 更新团队
          this.serviceProxy.update(submitData)
            .then(() => {
              this.$message.success(this.l('AuditTeam_UpdateSuccess'))
              this.handleCancel()
              this.$emit('refresh')
            })
            .catch(error => {
              console.error('更新团队失败:', error)
              this.$message.error(this.l('AuditTeam_UpdateFailed') + (error.message || error))
            })
            .finally(() => {
              this.confirmLoading = false
            })
        } else {
          // 创建团队
          this.serviceProxy.create(submitData)
            .then(() => {
              this.$message.success(this.l('AuditTeam_CreateSuccess'))
              this.handleCancel()
              this.$emit('refresh')
            })
            .catch(error => {
              console.error('创建团队失败:', error)
              this.$message.error(this.l('AuditTeam_CreateFailed') + (error.message || error))
            })
            .finally(() => {
              this.confirmLoading = false
            })
        }
      })
    },

    // 取消操作
    handleCancel() {
      this.visible = false
      this.form.resetFields()
      this.editingId = null
      this.isEdit = false
    }
  },
  beforeCreate() {
    this.form = this.$form.createForm(this)
  }
}
</script>

<style lang="less" scoped>
.user-option {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;

  .user-name {
    font-weight: 500;
    color: #1890ff;
  }

  .real-name {
    color: #666;
    font-size: 12px;
  }

  .job-number {
    color: #999;
    font-size: 12px;
  }
}

.help-text {
  color: #999;
  font-size: 12px;

  .anticon {
    margin-right: 4px;
  }
}

.ant-divider-horizontal.ant-divider-with-text-left {
  margin: 16px 0;

  .ant-divider-inner-text {
    font-size: 14px;
    font-weight: 500;
    color: #1890ff;
  }
}
</style>